import React, { FC, useCallback, useMemo, useState } from 'react';
import { buyerSettingsRestCrudThunksForBuyer } from '../../thunks/buyer_settings_thunks';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { message } from 'antd';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import UnsubscribeCard from './UnsubscribeCard';
import UnsubscribeSuccessCard from './UnsubscribeSuccessCard';
import { withUserType } from '../withUserType';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { supplierSettingsRestCrudThunksForSupplier } from '../../thunks/supplier_settings_thunks';
import { getNotificationDetailsFromKey } from '../../utils/NotificationUtils';

const NotificationsUnsubscribePage: FC<any> = ({
	match,
	settings,
	updateSettings,
	history,
	userType,
}): React.ReactElement => {
	const [unsubscribing, setUnsubscribing] = useState(false);

	const notificationType = match.params.notificationType;

	const notificationDetails = getNotificationDetailsFromKey(userType)(notificationType);

	if (!notificationDetails) {
		history.replace(`/${userType}/settings/notifications`);
	}

	const existingRootNotificationConfig = useMemo(
		() =>
			notificationDetails && nullSafeGetOrElse(notificationDetails.notificationKey, settings, {}),
		[settings, notificationDetails]
	);

	const existingNotificationConfig = useMemo(
		() =>
			notificationDetails && nullSafeGet(notificationDetails.key, existingRootNotificationConfig),
		[existingRootNotificationConfig, notificationDetails]
	);

	const isUnsubscribed = useMemo(
		() => (existingNotificationConfig ? existingNotificationConfig.email === false : false),
		[existingNotificationConfig]
	);

	const onUnsubscribe = useCallback(() => {
		const newSettings = {
			...settings,
			[notificationDetails.notificationKey]: {
				...existingRootNotificationConfig,
				[notificationDetails.key]: {
					sms: false,
					email: false,
					inApp: existingNotificationConfig ? existingNotificationConfig.inApp : true,
					important: nullSafeGetOrElse('important', existingNotificationConfig, false),
				},
			},
		};
		setUnsubscribing(true);
		updateSettings(newSettings)
			.then(() => message.success('Unsubscribed successfully'))
			.catch(message.error)
			.finally(() => setUnsubscribing(false));
	}, [
		settings,
		existingNotificationConfig,
		notificationDetails,
		existingRootNotificationConfig,
		updateSettings,
	]);

	const onChangePreference = useCallback(() => {
		history.replace(`/${userType}/notifications/${notificationType}/preferences`);
	}, [history, notificationType, userType]);

	const onCancel = useCallback(() => {
		history.replace('/');
	}, [history]);

	if (!notificationDetails) {
		return null;
	}

	return isUnsubscribed ? (
		<UnsubscribeSuccessCard
			notificationDetails={notificationDetails}
			onChangePreference={onChangePreference}
		/>
	) : (
		<UnsubscribeCard
			notificationDetails={notificationDetails}
			unsubscribing={unsubscribing}
			onUnsubscribe={onUnsubscribe}
			onCancel={onCancel}
		/>
	);
};

const mapStateToProps = (state, ownProps) => ({
	settings:
		ownProps.userType === ROLE_TYPES.SUPPLIER
			? state.supplier_settings.detail
			: state.buyer_settings.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateSettings: (settings) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? supplierSettingsRestCrudThunksForSupplier.update(settings)
				: buyerSettingsRestCrudThunksForBuyer.update(settings)
		),
});

export default withRouter<any, any>(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(NotificationsUnsubscribePage))
);
